import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@material-ui/core';
import { useAristotleSession, SESSION_ERRORS } from './useAristotleSession';
import { usePageContextPublisher } from './usePageContextPublisher';
import { parseAristotleMessage, buildHostMessage, HOST_MESSAGES, ARISTOTLE_MESSAGES } from './messages';

const ERROR_COPY = {
    [SESSION_ERRORS.USER_NOT_FOUND]:
        "Your account isn't set up in Aristotle yet. Ask your administrator to add you, then try again.",
    [SESSION_ERRORS.NO_EMAIL]:
        'Your Elements account has no email address, which Aristotle needs. Ask your administrator to add one.',
    [SESSION_ERRORS.NOT_CONFIGURED]:
        'The Aristotle assistant is not configured for this institution.',
    [SESSION_ERRORS.UPSTREAM]:
        'Aristotle is unreachable right now. Try again shortly.',
};

// PowerBI iframe sandbox precedent + popups/downloads (chat can export files
// and open documentation links in a new tab).
const IFRAME_SANDBOX =
    'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads';

const centerStyle = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 24,
    textAlign: 'center',
    fontSize: 14,
    color: '#424B5C',
    background: '#FFFFFF',
};

/**
 * The Aristotle embed iframe plus its host-side bridge: mints the one-time
 * code, loads {webUrl}/embed/collaborate?code=...&variant=..., answers the
 * iframe's ready / request-code messages (origin-checked), and publishes
 * host:context as the user moves around the SIS. Shared by the floating
 * widget (variant="widget") and the /assistant full page (variant="page").
 */
const AristotleFrame = ({ variant, visible = true, onClose, onUnavailable }) => {
    const iframeRef = useRef(null);
    const { mint } = useAristotleSession();
    const [boot, setBoot] = useState({ status: 'loading', errorCode: null });
    const [frame, setFrame] = useState({ src: null, origin: null, product: null });
    const [frameReady, setFrameReady] = useState(false);

    const startSession = useCallback(async () => {
        setBoot({ status: 'loading', errorCode: null });
        setFrameReady(false);
        try {
            const session = await mint();
            const origin = new URL(session.webUrl).origin;
            const src = `${origin}/embed/collaborate?code=${encodeURIComponent(session.code)}&variant=${encodeURIComponent(variant)}`;
            setFrame({ src, origin, product: session.product });
            setBoot({ status: 'ready', errorCode: null });
        } catch (error) {
            const code = error?.code || SESSION_ERRORS.UPSTREAM;
            setBoot({ status: 'error', errorCode: code });
            if (code === SESSION_ERRORS.NOT_CONFIGURED && onUnavailable) {
                onUnavailable();
            }
        }
    }, [mint, variant, onUnavailable]);

    useEffect(() => {
        startSession();
        // Mount-once by design: renewal happens in place via aristotle:request-code.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { publishNow } = usePageContextPublisher({
        iframeRef,
        targetOrigin: frame.origin,
        product: frame.product,
        enabled: frameReady,
    });

    const postToFrame = useCallback(
        (type, payload) => {
            const frameWindow = iframeRef.current?.contentWindow;
            if (!frameWindow || !frame.origin) return;
            frameWindow.postMessage(buildHostMessage(type, payload), frame.origin);
        },
        [frame.origin],
    );

    // Bridge: messages FROM the iframe, accepted only from the Aristotle origin.
    useEffect(() => {
        if (!frame.origin) return undefined;
        const onMessage = (event) => {
            if (event.origin !== frame.origin) return;
            const message = parseAristotleMessage(event.data);
            if (!message) return;
            if (message.type === ARISTOTLE_MESSAGES.READY) {
                setFrameReady(true);
            } else if (message.type === ARISTOTLE_MESSAGES.REQUEST_CODE) {
                // Token nearing expiry: silently re-mint and hand over a fresh
                // code; the iframe re-exchanges in place without reloading.
                mint()
                    .then((session) => postToFrame(HOST_MESSAGES.CODE, { code: session.code }))
                    .catch(() => {
                        // SIS session itself is dead or Aristotle is down — the
                        // global interceptor owns the 401 path; nothing to post.
                    });
            } else if (message.type === ARISTOTLE_MESSAGES.CLOSE) {
                if (onClose) onClose();
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [frame.origin, mint, postToFrame, onClose]);

    // First context snapshot rides the ready handshake.
    useEffect(() => {
        if (frameReady) publishNow();
    }, [frameReady, publishNow]);

    // Tell the iframe when the collapsed panel hides/shows it.
    useEffect(() => {
        if (frameReady) postToFrame(HOST_MESSAGES.VISIBILITY, { visible });
    }, [visible, frameReady, postToFrame]);

    if (boot.status === 'error') {
        return (
            <div style={centerStyle}>
                <div>{ERROR_COPY[boot.errorCode] || ERROR_COPY[SESSION_ERRORS.UPSTREAM]}</div>
                {boot.errorCode === SESSION_ERRORS.UPSTREAM && (
                    <button
                        type="button"
                        onClick={startSession}
                        style={{
                            border: '1px solid #6AC5DA',
                            background: '#FFFFFF',
                            color: '#424B5C',
                            borderRadius: 6,
                            padding: '6px 16px',
                            cursor: 'pointer',
                        }}
                    >
                        Try again
                    </button>
                )}
            </div>
        );
    }

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', background: '#FFFFFF' }}>
            {(boot.status === 'loading' || !frameReady) && (
                <div style={{ ...centerStyle, position: 'absolute', inset: 0 }}>
                    <CircularProgress style={{ color: '#6AC5DA' }} size={28} />
                    <div>Connecting…</div>
                </div>
            )}
            {frame.src && (
                <iframe
                    ref={iframeRef}
                    title="Aristotle assistant"
                    src={frame.src}
                    style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                    sandbox={IFRAME_SANDBOX}
                    allowFullScreen
                />
            )}
        </div>
    );
};

export default AristotleFrame;
